import React from "react";
import { motion } from "framer-motion";
import { Check, Building2, Layers, Globe2, Network } from "lucide-react";
import { useLang } from "@/lib/i18n";

const STAT_ICONS = [Building2, Layers, Globe2, Network];

export default function WhyX10() {
  const { t, dict } = useLang();
  const stats = dict.why.stats;
  const advantages = dict.why.advantages;

  return (
    <section id="why" data-testid="why-section" className="relative py-14 lg:py-20 bg-[#0A192F] text-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#1E3A8A] rounded-full blur-3xl opacity-30" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 lg:mb-20 text-center max-w-4xl mx-auto">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">{t("why.eyebrow")}</span>
          <h2 className="mt-6 font-heading font-bold text-3xl sm:text-4xl lg:text-6xl tracking-tight leading-[1.05]">
            {t("why.title")}
          </h2>
        </div>

        {/* Value props (mirroring hero footer style) — centered */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-20 lg:mb-24 max-w-6xl mx-auto">
          {stats.map((s, i) => {
            const Icon = STAT_ICONS[i % STAT_ICONS.length];
            return (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`why-stat-${i}`}
                className="group h-full flex flex-col items-center text-center p-4 sm:p-5 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#C9A96E]/50 hover:bg-white/[0.06] transition-all duration-300"
              >
                <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#C9A96E]/10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Icon size={16} className="text-[#C9A96E]" />
                </span>
                <div className="mt-3 sm:mt-4 font-heading text-[11px] sm:text-[13px] font-extrabold uppercase tracking-[0.14em] sm:tracking-[0.16em] text-white leading-tight whitespace-nowrap">
                  {s.t}
                </div>
                <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-white/70 leading-snug">
                  {s.s}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="space-y-12">
          <div className="max-w-3xl text-center lg:max-w-4xl mx-auto">
            <h3 className="font-heading text-2xl lg:text-3xl font-semibold leading-snug">{t("why.subTitle")}</h3>
            <p className="mt-3 text-slate-300 leading-relaxed">{t("why.subLead")}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4 lg:max-w-5xl lg:mx-auto">
            {advantages.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`advantage-${i}`}
                className="flex gap-5 p-6 rounded-xl border border-white/10 hover:border-[#E91E63]/60 hover:bg-white/5 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E91E63]/15 border border-[#E91E63]/50 flex items-center justify-center">
                  <Check size={18} className="text-[#E91E63]" />
                </div>
                <div>
                  <div className="font-heading text-lg font-semibold text-white">{a.title}</div>
                  <div className="mt-1 text-slate-300 text-sm leading-relaxed">{a.text}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
